import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

interface Webhook {
  id: string;
  url: string;
  events: string[];
  low_credit_threshold: number;
  is_active: boolean;
  failure_count: number;
  last_success_at: string | null;
  created_at: string;
  updated_at: string;
}

interface WebhooksResponse {
  data: Webhook[];
}

interface CreateResponse {
  data: Webhook & { signing_secret: string; warning: string };
}

const ALL_EVENTS = [
  { key: 'lead.created',                   label: 'Lead created' },
  { key: 'lead.expired',                   label: 'Lead expired' },
  { key: 'job.quote_accepted',             label: 'Quote accepted' },
  { key: 'job.quote_declined',             label: 'Quote declined' },
  { key: 'job.completed',                  label: 'Job completed' },
  { key: 'review.created',                 label: 'Review received' },
  { key: 'review.response_window_closing', label: 'Review response window closing' },
  { key: 'credit.balance_low',             label: 'Credit balance low' },
  { key: 'credit.refunded',               label: 'Credit refunded' },
] as const;

function generateSecret(): string {
  // eslint-disable-next-line no-secrets/no-secrets
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  return Array.from({ length: 32 }, () => charset[Math.floor(Math.random() * charset.length)]).join('');
}

export function WebhookManager() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [newUrl, setNewUrl] = useState('https://');
  const [newEvents, setNewEvents] = useState<string[]>(['lead.created']);
  const [newSecret, setNewSecret] = useState('');
  const [newThreshold, setNewThreshold] = useState(5);
  const [formError, setFormError] = useState<string | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contractor-webhooks'],
    queryFn: () => api.get<WebhooksResponse>('/contractor/webhooks'),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.post<CreateResponse>('/contractor/webhooks', {
        url: newUrl,
        events: newEvents,
        secret: newSecret,
        low_credit_threshold: newThreshold,
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['contractor-webhooks'] });
      setRevealedSecret(res.data.signing_secret);
      setShowForm(false);
      setNewUrl('https://');
      setNewEvents(['lead.created']);
      setNewSecret('');
      setNewThreshold(5);
      setFormError(null);
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? err.message : 'Failed to create webhook.');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.put(`/contractor/webhooks/${id}`, { is_active }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contractor-webhooks'] });
      setActionError(null);
    },
    onError: (err) => {
      setActionError(err instanceof ApiError ? err.message : 'Failed to update webhook.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/contractor/webhooks/${id}`),
    onSuccess: () => {
      setConfirmDeleteId(null);
      void queryClient.invalidateQueries({ queryKey: ['contractor-webhooks'] });
      setActionError(null);
    },
    onError: (err) => {
      setActionError(err instanceof ApiError ? err.message : 'Failed to delete webhook.');
    },
  });

  const testMutation = useMutation({
    mutationFn: () => api.post<{ message: string }>('/contractor/webhooks/test'),
    onSuccess: (res) => {
      setActionError(null);
      alert(res.message);
    },
    onError: (err) => {
      setActionError(err instanceof ApiError ? err.message : 'Test failed.');
    },
  });

  const webhooks = data?.data ?? [];

  function toggleEvent(event: string) {
    setNewEvents(prev =>
      prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event],
    );
  }

  function handleCopySecret() {
    if (!revealedSecret) return;
    navigator.clipboard.writeText(revealedSecret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => undefined);
  }

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: '#1D4ED8', padding: '48px 20px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate('/app/contractor/dashboard')}
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
              width: 36, height: 36, borderRadius: 10, cursor: 'pointer', fontSize: 18,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            &#8592;
          </button>
          <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>Webhooks</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={testMutation.isPending || webhooks.filter(w => w.is_active).length === 0}
            style={{
              padding: '8px 14px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff', borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}
          >
            {testMutation.isPending ? 'Testing…' : 'Test all'}
          </button>
          <button
            type="button"
            onClick={() => { setShowForm(true); setRevealedSecret(null); }}
            style={{
              padding: '8px 14px', background: '#fff', border: 'none',
              color: '#1D4ED8', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            + Add
          </button>
        </div>
      </div>

      <div style={{ padding: '16px 16px 100px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {actionError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 14px' }}>
            <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{actionError}</p>
          </div>
        )}

        {/* One-time secret reveal */}
        {revealedSecret && (
          <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 16, padding: 16 }}>
            <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: 13, color: '#15803d' }}>
              Webhook created — save your signing secret now
            </p>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: '#166534' }}>
              This secret will not be shown again. Use it to verify webhook signatures on your server.
            </p>
            <div style={{
              background: '#fff', border: '1px solid #bbf7d0', borderRadius: 8,
              padding: '10px 12px', fontFamily: 'monospace', fontSize: 13, color: '#111827',
              wordBreak: 'break-all', marginBottom: 10,
            }}>
              {revealedSecret}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleCopySecret}
                style={{
                  flex: 1, padding: '8px 0', background: copied ? '#22c55e' : '#15803d',
                  border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {copied ? 'Copied!' : 'Copy secret'}
              </button>
              <button
                type="button"
                onClick={() => setRevealedSecret(null)}
                style={{
                  padding: '8px 14px', background: 'none', border: '1px solid #86efac',
                  borderRadius: 8, color: '#15803d', fontSize: 13, cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Add form */}
        {showForm && (
          <div style={{ background: '#fff', borderRadius: 16, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.09)' }}>
            <p style={{ margin: '0 0 14px', fontWeight: 700, fontSize: 15, color: '#111827' }}>
              New webhook
            </p>

            {formError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
                <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{formError}</p>
              </div>
            )}

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Endpoint URL (HTTPS required)
            </label>
            <input
              type="url"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="https://your-server.com/webhooks/homeguard"
              style={{
                width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb',
                borderRadius: 8, fontSize: 13, outline: 'none',
                boxSizing: 'border-box', marginBottom: 14, color: '#111827',
              }}
            />

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
              Events to receive
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {ALL_EVENTS.map(({ key, label }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newEvents.includes(key)}
                    onChange={() => toggleEvent(key)}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#1D4ED8' }}
                  />
                  <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
                </label>
              ))}
            </div>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Signing secret (min 16 chars)
            </label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <input
                type="text"
                value={newSecret}
                onChange={e => setNewSecret(e.target.value)}
                placeholder="Your signing secret"
                style={{
                  flex: 1, padding: '9px 12px', border: '1.5px solid #e5e7eb',
                  borderRadius: 8, fontSize: 13, outline: 'none', color: '#111827',
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                onClick={() => setNewSecret(generateSecret())}
                style={{
                  padding: '9px 12px', background: '#f3f4f6', border: 'none',
                  borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap',
                }}
              >
                Generate
              </button>
            </div>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Low credit threshold
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={newThreshold}
              onChange={e => setNewThreshold(parseInt(e.target.value) || 0)}
              style={{
                width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb',
                borderRadius: 8, fontSize: 13, outline: 'none',
                boxSizing: 'border-box', marginBottom: 16, color: '#111827',
              }}
            />

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => { setShowForm(false); setFormError(null); }}
                style={{
                  flex: 1, padding: '10px 0', background: '#f3f4f6', border: 'none',
                  borderRadius: 10, fontSize: 14, cursor: 'pointer', color: '#374151', fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || !newUrl.startsWith('https://') || newEvents.length === 0 || newSecret.length < 16}
                style={{
                  flex: 2, padding: '10px 0',
                  background: !createMutation.isPending && newUrl.startsWith('https://') && newEvents.length > 0 && newSecret.length >= 16
                    ? '#1D4ED8' : '#e5e7eb',
                  border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  color: !createMutation.isPending && newUrl.startsWith('https://') && newEvents.length > 0 && newSecret.length >= 16
                    ? '#fff' : '#9ca3af',
                }}
              >
                {createMutation.isPending ? 'Creating…' : 'Create webhook'}
              </button>
            </div>
          </div>
        )}

        {/* Webhook list */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div style={{ width: 28, height: 28, border: '3px solid #1D4ED8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : webhooks.length === 0 && !showForm ? (
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#9ca3af' }}>
              No webhooks configured yet.
            </p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              style={{ padding: '10px 20px', background: '#1D4ED8', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              Add your first webhook
            </button>
          </div>
        ) : (
          webhooks.map(wh => (
            <div
              key={wh.id}
              style={{
                background: '#fff', borderRadius: 16, padding: 16,
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                border: wh.is_active ? '1.5px solid transparent' : '1.5px solid #fecaca',
              }}
            >
              {/* URL + status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <p style={{ margin: 0, fontSize: 12, fontFamily: 'monospace', color: '#374151', wordBreak: 'break-all', flex: 1 }}>
                  {wh.url}
                </p>
                <button
                  type="button"
                  onClick={() => toggleMutation.mutate({ id: wh.id, is_active: !wh.is_active })}
                  disabled={toggleMutation.isPending}
                  style={{
                    flexShrink: 0, padding: '4px 10px', border: 'none', borderRadius: 99,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: wh.is_active ? '#f0fdf4' : '#fef2f2',
                    color: wh.is_active ? '#15803d' : '#dc2626',
                  }}
                >
                  {wh.is_active ? 'Active' : 'Disabled'}
                </button>
              </div>

              {/* Failure warning */}
              {wh.failure_count > 0 && (
                <div style={{
                  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
                  padding: '6px 10px', marginBottom: 8,
                }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#92400e' }}>
                    {wh.failure_count} consecutive failure{wh.failure_count !== 1 ? 's' : ''}
                    {!wh.is_active ? ' — disabled automatically' : ` (disabled after ${5 - wh.failure_count} more)`}
                  </p>
                </div>
              )}

              {/* Events */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                {wh.events.map(evt => (
                  <span key={evt} style={{
                    fontSize: 11, fontFamily: 'monospace',
                    background: '#f3f4f6', color: '#374151',
                    padding: '2px 7px', borderRadius: 4,
                  }}>
                    {evt}
                  </span>
                ))}
              </div>

              {/* Footer row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>
                  {wh.last_success_at
                    ? `Last delivery ${new Date(wh.last_success_at).toLocaleDateString()}`
                    : 'No successful deliveries yet'}
                </p>

                {confirmDeleteId === wh.id ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      style={{ padding: '4px 10px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#6b7280' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(wh.id)}
                      disabled={deleteMutation.isPending}
                      style={{ padding: '4px 10px', background: '#dc2626', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#fff' }}
                    >
                      {deleteMutation.isPending ? '…' : 'Delete'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(wh.id)}
                    style={{ padding: '4px 10px', border: '1px solid #fecaca', background: '#fff', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#dc2626' }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
